<template>
  <div class="cat-simulator">
    <h4>Configuration CAT (Computer Adaptive Testing)</h4>
    <div class="row">
      <div class="col-md-4">
        <div class="form-floating mb-3">
          <select id="paliers" class="form-select" v-model="catParameters.paliers" @change="calculateItems">
            <option value="4">4</option>
            <option value="5">5</option>
            <option value="6">6</option>
          </select>
          <label for="paliers">Nombre de paliers</label>
        </div>
      </div>

      <div class="col-md-4">
        <div class="form-floating mb-3">
          <select id="confirmation" class="form-select" v-model="catParameters.confirmation" @change="calculateItems">
            <option value="3">3</option>
            <option value="4">4</option>
            <option value="5">5</option>
          </select>
          <label for="confirmation">Seuil de confirmation</label>
        </div>
      </div>

      <div class="col-md-4">
        <div class="form-floating mb-3">
          <input type="number" id="echec" class="form-control" v-model.number="catParameters.echec"
            @input="calculateItems" min="1" />
          <label for="echec">Seuil d'échec au palier 1</label>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <div class="form-check form-switch">
          <input class="form-check-input" type="checkbox" id="randomizedItemOrder"
            v-model="catParameters.randomizedItemOrder" @change="calculateItems">
          <label class="form-check-label" for="randomizedItemOrder">
            <strong>Randomized Item Order</strong>
            <small class="text-muted d-block">Items within each level will be presented in random order</small>
          </label>
        </div>
      </div>
    </div>

    <div class="result-container" v-if="calculatedItems > 0">
      <div class="alert alert-info">
        <strong>Nombre d'items requis : {{ calculatedItems }}</strong>
      </div>

      <!-- Interface de critères de recherche -->
      <div class="search-criteria-section mb-4">
        <h6><i class="fas fa-filter me-2"></i>Critères de recherche :</h6>
        
        <div class="criteria-grid">
          <!-- Critères principaux -->
          <div class="criteria-group">
            <h6 class="text-muted mb-2">Critères principaux :</h6>
            
            <div class="form-check">
              <input 
                class="form-check-input" 
                type="checkbox" 
                id="useISCED" 
                v-model="searchFilters.useISCED"
                :disabled="!formData.ISCED || formData.ISCED === 'ISCED-2'"
              >
              <label class="form-check-label" for="useISCED">
                ISCED: {{ formData.ISCED || 'ISCED-2' }}
                <small v-if="!formData.ISCED || formData.ISCED === 'ISCED-2'" class="text-muted">(valeur par défaut)</small>
              </label>
            </div>
            
            <div class="form-check">
              <input 
                class="form-check-input" 
                type="checkbox" 
                id="useSubject" 
                v-model="searchFilters.useSubject"
                :disabled="!formData.Subject || formData.Subject === 'general'"
              >
              <label class="form-check-label" for="useSubject">
                Subject: {{ formData.Subject || 'general' }}
                <small v-if="!formData.Subject || formData.Subject === 'general'" class="text-muted">(valeur par défaut)</small>
              </label>
            </div>
            
            <div class="form-check">
              <input 
                class="form-check-input" 
                type="checkbox" 
                id="useDomain" 
                v-model="searchFilters.useDomain"
                :disabled="!formData.domain"
              >
              <label class="form-check-label" for="useDomain">
                Domain: {{ formData.domain || 'non spécifié' }}
                <small v-if="!formData.domain" class="text-muted">(non défini)</small>
              </label>
            </div>
            
            <div class="form-check">
              <input 
                class="form-check-input" 
                type="checkbox" 
                id="useDifficultyRange" 
                v-model="searchFilters.useDifficultyRange"
              >
              <label class="form-check-label" for="useDifficultyRange">
                Difficulty range: 1 - {{ catParameters.paliers }}
              </label>
            </div>
          </div>
          
          <!-- Keywords avec contrôle individuel -->
          <div v-if="availableKeywords.length > 0" class="criteria-group">
            <h6 class="text-muted mb-2">Keywords :</h6>
            <div class="keywords-list">
              <div v-for="(keyword, index) in availableKeywords" :key="index" class="form-check">
                <input 
                  class="form-check-input" 
                  type="checkbox" 
                  :id="`keyword_${index}`" 
                  v-model="searchFilters.selectedKeywords[index]"
                >
                <label class="form-check-label" :for="`keyword_${index}`">
                  {{ keyword }}
                </label>
              </div>
            </div>
            
            <!-- Boutons pour tout sélectionner/désélectionner -->
            <div class="mt-2">
              <button type="button" class="btn btn-sm btn-outline-secondary me-2" @click="selectAllKeywords">
                Tout sélectionner
              </button>
              <button type="button" class="btn btn-sm btn-outline-secondary" @click="deselectAllKeywords">
                Tout désélectionner
              </button>
            </div>
          </div>
        </div>
        
        <!-- Résumé des critères actifs -->
        <div class="mt-3 p-2 bg-light rounded">
          <small class="text-muted">
            <strong>Critères actifs :</strong> {{ getActiveCriteriaCount() }} sur {{ getTotalCriteriaCount() }}
            <span v-if="getActiveCriteriaCount() === 0" class="text-warning">
              (⚠️ Aucun critère sélectionné - recherche très large)
            </span>
          </small>
        </div>
      </div>

      <!-- Recherche d'items -->
      <div class="search-section mb-4">
        <button 
          v-if="!isSearching" 
          @click="launchItemSearch" 
          type="button" 
          class="btn btn-primary"
        >
          <i class="fas fa-search"></i> 
          {{ itemsFound ? 'Search Again' : 'Launch Item Research' }}
        </button>
        
        <button v-if="isSearching" class="btn btn-primary" type="button" disabled>
          <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          Searching items...
        </button>
        
        <!-- Bouton pour réinitialiser la recherche -->
        <button 
          v-if="itemsFound && !isSearching" 
          @click="resetSearch" 
          type="button" 
          class="btn btn-outline-secondary ms-2"
        >
          <i class="fas fa-undo"></i> Reset Search
        </button>
      </div>

      <!-- Résultats de recherche -->
      <div v-if="itemsFound" class="search-results">
        <div class="alert" :class="foundItems.length > 0 ? 'alert-success' : 'alert-warning'">
          <strong>{{ foundItems.length }} items trouvés dans la base de données</strong>
          <span v-if="foundItems.length === 0" class="d-block mt-1">
            <i class="fas fa-exclamation-triangle me-1"></i>
            Aucun item ne correspond aux critères de recherche. Vérifiez les paramètres ci-dessous.
          </span>
          <span v-else-if="foundItems.length < calculatedItems" class="d-block mt-1">
            <i class="fas fa-info-circle me-1 text-warning"></i>
            Seulement {{ foundItems.length }} items trouvés, mais {{ calculatedItems }} nécessaires pour le CAT. 
            Vous pouvez continuer avec moins d'items ou réduire les paramètres CAT.
          </span>
          <span v-else class="d-block mt-1">
            <i class="fas fa-check-circle me-1"></i>
            Suffisamment d'items trouvés pour créer le test CAT ({{ calculatedItems }} nécessaires).
          </span>
        </div>

        <!-- Debug: Affichage des critères de recherche -->
        <div v-if="lastSearchCriteria" class="alert alert-info">
          <h6><i class="fas fa-search me-2"></i>Search Criteria Used:</h6>
          <div class="row">
            <div class="col-md-6">
              <ul class="list-unstyled mb-0">
                <li><strong>CAT Algorithm needs:</strong> {{ calculatedItems }} items minimum</li>
                <li><strong>Search requested:</strong> {{ lastSearchCriteria.required_items }} items (with buffer)</li>
                <li v-if="lastSearchCriteria.difficulty_range"><strong>Difficulty range:</strong> {{ lastSearchCriteria.difficulty_range?.min }} - {{ lastSearchCriteria.difficulty_range?.max }}</li>
                <li v-if="lastSearchCriteria.ISCED"><strong>ISCED:</strong> {{ lastSearchCriteria.ISCED }}</li>
                <li v-if="lastSearchCriteria.subject"><strong>Subject:</strong> {{ lastSearchCriteria.subject }}</li>
              </ul>
            </div>
            <div class="col-md-6">
              <ul class="list-unstyled mb-0">
                <li v-if="lastSearchCriteria.keywords && lastSearchCriteria.keywords.length > 0">
                  <strong>Keywords:</strong> {{ lastSearchCriteria.keywords.join(', ') }}
                </li>
                <li v-else><strong>Keywords:</strong> <em>No keywords specified</em></li>
                <li v-if="lastSearchCriteria.selectedKeywordsDebug" class="text-muted mt-1">
                  <small><strong>Keywords debug:</strong> {{ lastSearchCriteria.selectedKeywordsDebug }}</small>
                </li>
                <li v-if="lastSearchCriteria.note" class="text-muted mt-2">
                  <strong>Search type:</strong> {{ lastSearchCriteria.note }}
                </li>
              </ul>
            </div>
          </div>
          
          <!-- Explication du calcul CAT -->
          <div class="mt-2 p-2 bg-light rounded">
            <small class="text-muted">
              <strong>CAT Algorithm calculation:</strong> 
              {{ catParameters.paliers }} levels × {{ catParameters.confirmation }} confirmation{{ catParameters.confirmation > 1 ? 's' : '' }} 
              + {{ catParameters.echec }} failure threshold = {{ calculatedItems }} items needed
            </small>
          </div>
          
          <!-- Bouton de recherche élargie si aucun item trouvé -->
          <div v-if="foundItems.length === 0" class="mt-3 text-center">
            <button 
              @click="launchBroadSearch" 
              class="btn btn-outline-info"
              :disabled="isSearching"
            >
              <i class="fas fa-search-plus me-2"></i>
              Try Broader Search (no subject/keywords filters)
            </button>
          </div>
        </div>

        <!-- Tableau des items trouvés -->
        <div class="items-table-section mb-4">
          <h5>Items Found - Difficulty Calibration</h5>
          <div class="table-responsive">
            <table class="table table-striped table-hover">
              <thead class="table-dark">
                <tr>
                  <th>ID</th>
                  <th>Source Test</th>
                  <th>Type</th>
                  <th>Text Preview</th>
                  <th>Creator Rating</th>
                  <th>Psychometrics</th>
                  <th class="text-muted">AI Module (disabled)</th>
                  <th>Synthetic</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in foundItems" :key="item._id">
                  <td><small>{{ item._id }}</small></td>
                  <td>
                    <strong>{{ item.test_name || 'Unknown Test' }}</strong>
                    <small class="d-block text-muted">{{ item.test_id }}</small>
                  </td>
                  <td><span class="badge bg-secondary">{{ item.el_type }}</span></td>
                  <td class="text-truncate" style="max-width: 200px;">{{ item.el_text }}</td>
                  <td>
                    <span class="badge" :class="getDifficultyBadgeClass(item.creator_difficulty)">
                      {{ item.creator_difficulty }}
                    </span>
                  </td>
                  <td>
                    <span v-if="item.psychometric_reports > 0" class="badge bg-info">
                      {{ item.psychometric_success_rate }}% ({{ item.psychometric_reports }} reports)
                    </span>
                    <span v-else class="text-muted">No data</span>
                  </td>
                  <td class="text-muted">
                    <i class="fas fa-lock"></i> Disabled
                  </td>
                  <td>
                    <span class="badge" :class="getDifficultyBadgeClass(item.synthetic_difficulty)">
                      {{ item.synthetic_difficulty }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Options de calibration -->
        <div class="calibration-section mb-4">
          <h6>Choose Calibration Source:</h6>
          <div class="btn-group" role="group">
            <input type="radio" class="btn-check" name="calibrationSource" id="creator" value="creator" v-model="selectedCalibration">
            <label class="btn btn-outline-primary" for="creator">Creator Rating</label>

            <input type="radio" class="btn-check" name="calibrationSource" id="psychometric" value="psychometric" v-model="selectedCalibration">
            <label class="btn btn-outline-info" for="psychometric">Psychometrics</label>

            <input type="radio" class="btn-check" name="calibrationSource" id="synthetic" value="synthetic" v-model="selectedCalibration" checked>
            <label class="btn btn-outline-success" for="synthetic">Synthetic (Recommended)</label>
          </div>
          
          <button @click="calibrateDifficulty" class="btn btn-success ms-3">
            <i class="fas fa-cogs"></i> Calibrate Difficulty
          </button>
        </div>

        <!-- Graphique de répartition -->
        <div v-if="isCalibrated" class="distribution-section">
          <!-- Contrôles du graphique -->
          <div class="text-center mb-3">
            <div class="btn-group" role="group">
              <button type="button" class="btn btn-outline-primary btn-sm" @click="calibrateDifficulty">
                <i class="fas fa-search"></i> Show Found Items
              </button>
              <button 
                type="button" 
                class="btn btn-outline-success btn-sm" 
                @click="updateGraphFromCATStructure"
                :disabled="!testStore.testData?.CATStructure"
              >
                <i class="fas fa-sync-alt"></i> Show Test Structure
              </button>
            </div>
            <p class="text-muted small mt-2">
              <strong>Found Items</strong>: Distribution of searched items | 
              <strong>Test Structure</strong>: Current CAT test distribution
            </p>
          </div>
          
          <div class="row">
            <div class="col-md-6">
              <div class="chart-container">
                <h6>Item Distribution by Difficulty Level</h6>
                <canvas ref="distributionChart" width="400" height="300"></canvas>
              </div>
            </div>
            <div class="col-md-6">
              <div class="stats-container">
                <h6>Distribution Summary</h6>
                <div class="list-group">
                  <div v-for="(count, level) in itemDistribution" :key="level" class="list-group-item d-flex justify-content-between">
                    <span>Level {{ level }}</span>
                    <span class="badge bg-primary">{{ count }} items</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Items manquants -->
          <div v-if="missingItemsCount > 0" class="alert alert-warning mt-3">
            <h6><i class="fas fa-exclamation-triangle"></i> Missing Items Analysis</h6>
            <p><strong>{{ missingItemsCount }} items manquants pour une répartition optimale</strong></p>
            <div class="row">
              <div class="col-md-6">
                <canvas ref="missingChart" width="400" height="200"></canvas>
              </div>
              <div class="col-md-6">
                <div class="missing-stats">
                  <div v-for="(missing, level) in missingItems" :key="level" class="d-flex justify-content-between mb-2">
                    <span>Level {{ level }}:</span>
                    <span class="badge bg-warning">{{ missing }} manquants</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Bouton d'action finale simplifié -->
          <div class="text-center mt-4">
            <div class="alert alert-success mb-3">
              <h6><i class="fas fa-check-circle me-2"></i>Items Ready for CAT Test</h6>
              <p class="mb-0">
                <strong>{{ foundItems.length }} items</strong> from Item Bank are ready to be organized into your CAT test structure.
                <br>These items will be distributed across <strong>{{ catParameters.paliers }} difficulty levels</strong> for adaptive testing.
              </p>
            </div>
            
            <button 
              @click="validateAndDistribute" 
              class="btn btn-lg btn-success"
              :disabled="isProcessing"
            >
              <span v-if="isProcessing" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              <i v-else class="fas fa-magic me-2"></i>
              {{ isProcessing ? 'Preparing CAT Test...' : 'Prepare CAT Test Structure' }}
            </button>
            
            <p class="text-muted small mt-3">
              <i class="fas fa-info-circle me-1"></i>
              Your {{ foundItems.length }} found items will be distributed by difficulty level and integrated into the test structure.
            </p>
          </div>
        </div>
        
        <!-- Section finale de création du test CAT -->
        <div v-if="isDistributed" class="cat-creation-section mt-5">
          <div class="card border-success">
            <div class="card-header bg-success text-white">
              <h5 class="mb-0">
                <i class="fas fa-check-circle me-2"></i>
                CAT Test Ready for Creation
              </h5>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-8">
                  <h6>Test Configuration Summary:</h6>
                  <ul class="list-unstyled">
                    <li><i class="fas fa-tag text-success me-2"></i><strong>Title:</strong> {{ getAutoGeneratedTitle() }}</li>
                    <li v-if="formData.ISCED"><i class="fas fa-graduation-cap text-info me-2"></i><strong>ISCED:</strong> {{ formData.ISCED }}</li>
                    <li v-if="formData.Subject"><i class="fas fa-book text-primary me-2"></i><strong>Subject:</strong> {{ formData.Subject }}</li>
                    <li v-if="formData.domain"><i class="fas fa-sitemap text-secondary me-2"></i><strong>Domain:</strong> {{ formData.domain }}</li>
                    <li v-if="formData.duration"><i class="fas fa-clock text-warning me-2"></i><strong>Duration:</strong> {{ formData.duration }}</li>
                    <li><i class="fas fa-layer-group text-primary me-2"></i><strong>{{ catParameters.paliers }}</strong> difficulty levels</li>
                    <li><i class="fas fa-cubes text-info me-2"></i><strong>{{ foundItems.length }}</strong> items from Item Bank</li>
                    <li><i class="fas fa-cogs text-warning me-2"></i>Calibration: <strong>{{ selectedCalibration }}</strong></li>
                    <li><i class="fas fa-random text-secondary me-2"></i>Order: <strong>{{ catParameters.randomizedItemOrder ? 'Randomized' : 'Fixed' }}</strong></li>
                  </ul>
                  <p class="text-muted small">
                    Items have been distributed across difficulty levels and are ready to be converted into a functional CAT test.
                  </p>
                </div>
                <div class="col-md-4 text-center">
                  <button 
                    @click="createCATTest" 
                    class="btn btn-success btn-lg"
                    :disabled="isCreatingTest"
                  >
                    <span v-if="isCreatingTest" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    <i v-else class="fas fa-plus-circle me-2"></i>
                    {{ isCreatingTest ? 'Creating Test...' : 'Create CAT Test' }}
                  </button>
                  <br>
                  <small class="text-muted mt-2 d-block">
                    This will create the final test with real items
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Textes de brouillon commentés -->
      <!--
      <div>Choose how to calibrate the item difficulty</div>
      En ligne : par item trouvés
      En colonne: _id | el_ID | el_type |el_Text |
      Results table Colonne 1 : By default - defined by the item creator |
      Results table Colonne 2+ : for psychometrics - success rate - depending of how many reports are available|
      Results table Colonne suivante (disabled) : for AI module |
      Results table Colonne suivante : Synthetic calibration (mix of 1 and 2)
      Graphique chart js de répartition en fonction des niveaux demandés
      niveau 1: X items, niveau 2 : x items, niveau 3....
      Le niveau est le niveau de difficulté et les items sont répartis en fonction de leur difficulté.
      niveau 1: X items manquant, niveau 2 : x items manquant, niveau 3....
      -->
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { Chart, registerables } from 'chart.js'
import api from '@/services/axios'
import { useTestStore } from '@/stores/testStore'

// Enregistrer tous les composants Chart.js
Chart.register(...registerables)

// Props pour recevoir les paramètres existants
const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({
      paliers: 4,
      confirmation: 3,
      echec: 3,
      randomizedItemOrder: true
    })
  },
  showRebuildButton: {
    type: Boolean,
    default: false
  },
  formData: {
    type: Object,
    default: () => ({})
  }
})

// Événement pour émettre les changements
const emit = defineEmits(['update:modelValue', 'itemsDistributed', 'cat-rebuilt', 'cat-test-created'])

// Store pour accéder aux données du test
const testStore = useTestStore()

// Computed pour accès facile aux données du formulaire dans le template
const formData = computed(() => props.formData || {})

// Paramètres CAT locaux
const catParameters = ref({
  paliers: props.modelValue.paliers || 4,
  confirmation: props.modelValue.confirmation || 3,
  echec: props.modelValue.echec || 3,
  randomizedItemOrder: props.modelValue.randomizedItemOrder !== undefined ? props.modelValue.randomizedItemOrder : true
})

// Calcul du nombre d'items
const calculatedItems = ref(0)
const isUpdatingFromProps = ref(false) // Protection contre les boucles

// État de la recherche et calibration
const isSearching = ref(false)
const itemsFound = ref(false)
const foundItems = ref([])
const selectedCalibration = ref('synthetic')
const isCalibrated = ref(false)
const itemDistribution = ref({})
const missingItems = ref({})
const isDistributed = ref(false)
const isCreatingTest = ref(false)
const isProcessing = ref(false)
const distributedItemsData = ref(null) // Stocker les données distribuées localement
const distributionChart = ref(null)
const missingChart = ref(null)
let chartInstance = null
let missingChartInstance = null

// Debug: critères de recherche utilisés
const lastSearchCriteria = ref(null)

// Filtres de recherche contrôlés par l'utilisateur
const searchFilters = ref({
  useISCED: true,
  useSubject: true,
  useDomain: true,
  useDifficultyRange: true,
  selectedKeywords: []
})

// Computed property pour les keywords disponibles
const availableKeywords = computed(() => {
  const formData = props.formData || {}
  if (!formData.Keywords) return []
  
  const keywords = Array.isArray(formData.Keywords) 
    ? formData.Keywords 
    : typeof formData.Keywords === 'string'
      ? formData.Keywords.split(',').map(k => k.trim()).filter(k => k)
      : []
  
  // Initialiser selectedKeywords si nécessaire
  if (searchFilters.value.selectedKeywords.length !== keywords.length) {
    searchFilters.value.selectedKeywords = keywords.map(() => true)
  }
  
  return keywords
})

// Méthodes pour gérer les keywords
const selectAllKeywords = () => {
  searchFilters.value.selectedKeywords = availableKeywords.value.map(() => true)
}

const deselectAllKeywords = () => {
  searchFilters.value.selectedKeywords = availableKeywords.value.map(() => false)
}

// Méthodes pour compter les critères actifs
const getActiveCriteriaCount = () => {
  const formData = props.formData || {}
  let count = 0
  
  if (searchFilters.value.useISCED && formData.ISCED && formData.ISCED !== 'ISCED-2') count++
  if (searchFilters.value.useSubject && formData.Subject && formData.Subject !== 'general') count++
  if (searchFilters.value.useDomain && formData.domain) count++
  if (searchFilters.value.useDifficultyRange) count++
  
  // Compter les keywords sélectionnés
  const selectedKeywordsCount = searchFilters.value.selectedKeywords.filter(Boolean).length
  count += selectedKeywordsCount
  
  return count
}

const getTotalCriteriaCount = () => {
  const formData = props.formData || {}
  let count = 0
  
  if (formData.ISCED && formData.ISCED !== 'ISCED-2') count++
  if (formData.Subject && formData.Subject !== 'general') count++
  if (formData.domain) count++
  count++ // difficulty range toujours disponible
  count += availableKeywords.value.length
  
  return count
}

const calculateItems = () => {
  const paliers = parseInt(catParameters.value.paliers)
  const confirmation = parseInt(catParameters.value.confirmation)
  const echec = parseInt(catParameters.value.echec)

  const niveauDepart = Math.ceil(paliers / 2)
  const montee = paliers - niveauDepart + 1
  const descente = paliers
  const essaisParPalier = (confirmation - 1) * 2

  calculatedItems.value = montee * essaisParPalier + descente + (echec - 1) + confirmation

  // Ne pas émettre si on est en train de mettre à jour depuis les props
  if (isUpdatingFromProps.value) {
    console.log("🔄 CATSimulator: Calcul sans émission (sync props)")
    return
  }

  console.log("✅ CATSimulator: Calcul avec émission")
  // Émettre les paramètres mis à jour
  const updatedParameters = {
    ...catParameters.value,
    calculatedItems: calculatedItems.value
  }

  emit('update:modelValue', updatedParameters)
}

// Watcher pour synchroniser avec les props
watch(() => props.modelValue, (newValue) => {
  if (newValue) {
    isUpdatingFromProps.value = true
    catParameters.value = { ...newValue }
    calculateItems()
    // Réinitialiser le flag après un délai
    setTimeout(() => {
      isUpdatingFromProps.value = false
    }, 50)
  }
}, { deep: true, immediate: true })

// Watcher pour recalculer quand les paramètres changent
watch(catParameters, () => {
  calculateItems()
}, { deep: true })

// Computed pour le nombre d'items manquants
const missingItemsCount = computed(() => {
  return Object.values(missingItems.value).reduce((sum, count) => sum + count, 0)
})

// Recherche d'items dans la base de données CAT
const searchCATItems = async (searchCriteria) => {
  try {
    console.log('🔍 Searching CAT database with criteria:', searchCriteria)
    
    const response = await api.post('/cat/search', searchCriteria)
    
    if (response.data.success) {
      console.log(`✅ Found ${response.data.items.length} items in CAT database`)
      return response.data.items
    } else {
      throw new Error(response.data.error || 'Search failed')
    }
  } catch (error) {
    console.error('❌ CAT search error:', error)
    throw error
  }
}

// Fonction pour synchroniser le graphique avec la CATStructure réelle
const updateGraphFromCATStructure = () => {
  console.log('📊 Updating graph from actual CAT structure...')
  
  // Récupérer la structure CAT du test store
  const catStructure = testStore.testData?.CATStructure
  
  if (!catStructure || !catStructure.levels) {
    console.log('⚠️ No CAT structure found in test data')
    return
  }
  
  // Calculer la distribution réelle
  const distribution = {}
  const missing = {}
  const levelsCount = parseInt(catParameters.value.paliers)
  
  // Initialiser les niveaux
  for (let i = 1; i <= levelsCount; i++) {
    distribution[i] = 0
    missing[i] = 0
  }
  
  // Compter les items par niveau dans la structure réelle
  catStructure.levels.forEach(level => {
    if (level.items && Array.isArray(level.items)) {
      distribution[level.level] = level.items.length
    }
  })
  
  // Calculer les items manquants (répartition idéale)
  const targetPerLevel = Math.ceil(calculatedItems.value / levelsCount)
  for (let i = 1; i <= levelsCount; i++) {
    const shortfall = Math.max(0, targetPerLevel - distribution[i])
    missing[i] = shortfall
  }
  
  // Mettre à jour les données du graphique
  itemDistribution.value = distribution
  missingItems.value = missing
  isCalibrated.value = true
  
  console.log('📊 Distribution from CAT structure:', distribution)
  
  // Recréer les graphiques
  nextTick(() => {
    createDistributionChart()
    if (missingItemsCount.value > 0) {
      createMissingItemsChart()
    }
  })
}

// Méthode pour lancer la recherche d'items
const launchItemSearch = async () => {
  isSearching.value = true
  itemsFound.value = false
  
  try {
    // Construire les critères de recherche basés sur les filtres sélectionnés par l'utilisateur
    const formData = props.formData || {}
    const searchCriteria = {
      required_items: 100 // Demander tous les items disponibles
    }
    
    // Ajouter les critères seulement s'ils sont sélectionnés par l'utilisateur
    if (searchFilters.value.useDifficultyRange) {
      searchCriteria.difficulty_range = {
        min: 1,
        max: catParameters.value.paliers
      }
    }
    
    if (searchFilters.value.useISCED && formData.ISCED && formData.ISCED !== 'ISCED-2') {
      searchCriteria.ISCED = formData.ISCED
    }
    
    if (searchFilters.value.useSubject && formData.Subject && formData.Subject !== 'general') {
      searchCriteria.subject = formData.Subject
    }
    
    if (searchFilters.value.useDomain && formData.domain) {
      searchCriteria.domain = formData.domain
    }
    
    // Gérer les keywords sélectionnés individuellement
    const selectedKeywords = availableKeywords.value.filter((keyword, index) => 
      searchFilters.value.selectedKeywords[index]
    )
    
    if (selectedKeywords.length > 0) {
      searchCriteria.keywords = selectedKeywords
      console.log(`🔍 Selected keywords for search:`, selectedKeywords)
      console.log(`📝 Available keywords:`, availableKeywords.value)
      console.log(`✅ Checkbox states:`, searchFilters.value.selectedKeywords)
    }
    
    // Stocker les critères pour affichage debug
    lastSearchCriteria.value = {
      ...searchCriteria,
      note: `User-defined search (${getActiveCriteriaCount()} criteria active)`,
      selectedKeywordsDebug: selectedKeywords.length > 0 ? selectedKeywords : 'No keywords selected'
    }
    
    console.log('🎯 Starting CAT item search with user-defined criteria:', searchCriteria)
    console.log(`📊 Active criteria: ${getActiveCriteriaCount()} out of ${getTotalCriteriaCount()}`)
    
    // Recherche dans la base de données CAT
    const items = await searchCATItems(searchCriteria)
    
    foundItems.value = items
    isSearching.value = false
    itemsFound.value = true
    
    // Vérifier si nous avons assez d'items
    if (items.length < calculatedItems.value) {
      console.warn(`⚠️ Found only ${items.length} items, but need ${calculatedItems.value}`)
      console.warn('💡 Consider: 1) Uncheck some search criteria to find more items, 2) Add more items to Item Bank, or 3) Use fewer CAT levels')
    }
    
    console.log(`✅ CAT search completed: ${foundItems.value.length} items found with ${getActiveCriteriaCount()} active criteria`)
    
    // Log détaillé pour debug si aucun item trouvé
    if (items.length === 0) {
      console.warn('⚠️ No items found with current criteria. Try unchecking some filters to broaden your search.')
    }
    
  } catch (error) {
    console.error('❌ CAT search failed:', error)
    isSearching.value = false
    
    // Afficher un message d'erreur à l'utilisateur
    alert(`Search failed: ${error.message}\nPlease check the console for more details.`)
  }
}

// Méthode pour réinitialiser la recherche
const resetSearch = () => {
  itemsFound.value = false
  foundItems.value = []
  lastSearchCriteria.value = null
  isCalibrated.value = false
  isDistributed.value = false
  itemDistribution.value = {}
  missingItems.value = {}
  
  // Réinitialiser les graphiques
  if (chartInstance) {
    chartInstance.destroy()
    chartInstance = null
  }
  if (missingChartInstance) {
    missingChartInstance.destroy()  
    missingChartInstance = null
  }
  
  console.log('🔄 Search reset - ready for new criteria')
}

// Méthode pour lancer une recherche élargie (sans critères restrictifs)
const launchBroadSearch = async () => {
  isSearching.value = true
  
  try {
    const broadSearchCriteria = {
      required_items: calculatedItems.value + 20,
      difficulty_range: {
        min: 1,
        max: catParameters.value.paliers
      }
      // Pas de critères ISCED, subject ou keywords
    }
    
    lastSearchCriteria.value = broadSearchCriteria
    console.log('🎯 Starting broad CAT item search:', broadSearchCriteria)
    
    const items = await searchCATItems(broadSearchCriteria)
    
    foundItems.value = items
    isSearching.value = false
    itemsFound.value = true
    
    console.log(`✅ Broad search completed: ${foundItems.value.length} items found`)
    
    if (items.length === 0) {
      console.warn('⚠️ No items found even with broad search - CAT database may be empty')
    }
    
  } catch (error) {
    console.error('❌ Broad CAT search failed:', error)
    isSearching.value = false
    alert(`Broad search failed: ${error.message}`)
  }
}

// Méthode pour obtenir la classe CSS selon la difficulté
const getDifficultyBadgeClass = (difficulty) => {
  const classes = {
    1: 'bg-success',    // Vert pour facile
    2: 'bg-info',       // Bleu pour moyen-facile
    3: 'bg-warning',    // Jaune pour moyen
    4: 'bg-orange',     // Orange pour moyen-difficile
    5: 'bg-danger',     // Rouge pour difficile
    6: 'bg-dark'        // Noir pour très difficile
  }
  return classes[difficulty] || 'bg-secondary'
}

// Méthode pour calibrer la difficulté
const calibrateDifficulty = async () => {
  console.log(`🎯 Calibrating difficulty using: ${selectedCalibration.value}`)
  
  // Calculer la distribution selon la source choisie
  const distribution = {}
  const missing = {}
  const levelsCount = parseInt(catParameters.value.paliers)
  
  // Initialiser les niveaux
  for (let i = 1; i <= levelsCount; i++) {
    distribution[i] = 0
    missing[i] = 0
  }
  
  // Répartir les items selon la calibration choisie
  foundItems.value.forEach(item => {
    let difficulty
    switch (selectedCalibration.value) {
      case 'creator':
        difficulty = item.creator_difficulty
        break
      case 'psychometric':
        // Utiliser psychométrie si disponible, sinon créateur
        if (item.psychometric_reports > 0) {
          difficulty = Math.ceil((100 - item.psychometric_success_rate) / (100 / levelsCount))
        } else {
          difficulty = item.creator_difficulty
        }
        break
      case 'synthetic':
      default:
        difficulty = item.synthetic_difficulty
        break
    }
    
    if (difficulty >= 1 && difficulty <= levelsCount) {
      distribution[difficulty]++
    }
  })
  
  // Calculer les items manquants (répartition idéale)
  const targetPerLevel = Math.ceil(calculatedItems.value / levelsCount)
  for (let i = 1; i <= levelsCount; i++) {
    const shortfall = Math.max(0, targetPerLevel - distribution[i])
    missing[i] = shortfall
  }
  
  itemDistribution.value = distribution
  missingItems.value = missing
  isCalibrated.value = true
  
  // Créer les graphiques après mise à jour du DOM
  await nextTick()
  createDistributionChart()
  if (missingItemsCount.value > 0) {
    createMissingItemsChart()
  }
}

// Créer le graphique de distribution
const createDistributionChart = () => {
  if (chartInstance) {
    chartInstance.destroy()
  }
  
  const ctx = distributionChart.value.getContext('2d')
  const levels = Object.keys(itemDistribution.value)
  const counts = Object.values(itemDistribution.value)
  
  chartInstance = new Chart(ctx, {
    type: 'bar',
    data: {
      labels: levels.map(l => `Level ${l}`),
      datasets: [{
        label: 'Items Count',
        data: counts,
        backgroundColor: [
          '#28a745', '#17a2b8', '#ffc107', 
          '#fd7e14', '#dc3545', '#6c757d'
        ].slice(0, levels.length),
        borderColor: [
          '#1e7e34', '#138496', '#e0a800', 
          '#e55a00', '#c82333', '#5a6268'
        ].slice(0, levels.length),
        borderWidth: 1
      }]
    },
    options: {
      responsive: true,
      plugins: {
        legend: {
          display: false
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            stepSize: 1
          }
        }
      }
    }
  })
}

// Créer le graphique des items manquants
const createMissingItemsChart = () => {
  if (missingChartInstance) {
    missingChartInstance.destroy()
  }
  
  const ctx = missingChart.value.getContext('2d')
  const levels = Object.keys(missingItems.value).filter(level => missingItems.value[level] > 0)
  const counts = levels.map(level => missingItems.value[level])
  
  if (levels.length === 0) return
  
  missingChartInstance = new Chart(ctx, {
    type: 'doughnut',
    data: {
      labels: levels.map(l => `Level ${l}`),
      datasets: [{
        data: counts,
        backgroundColor: [
          '#ffc107', '#fd7e14', '#dc3545', 
          '#6f42c1', '#e83e8c', '#20c997'
        ].slice(0, levels.length)
      }]
    },
    options: {
      responsive: true,
      plugins: {
        legend: {
          position: 'bottom'
        }
      }
    }
  })
}

// Méthode pour valider et distribuer les items
const validateAndDistribute = async () => {
  isProcessing.value = true
  
  try {
    console.log('✅ Validating and distributing items to CAT Editor')
    
    if (!foundItems.value || foundItems.value.length === 0) {
      alert('No items found! Please run the search first.')
      return
    }
  
  // Organiser les items par niveau de difficulté selon la calibration choisie
  const organizedItems = {}
  const levelsCount = parseInt(catParameters.value.paliers)
  
  // Initialiser les niveaux
  for (let i = 1; i <= levelsCount; i++) {
    organizedItems[i] = []
  }
  
  // Distribuer les items selon la calibration choisie
  foundItems.value.forEach(item => {
    let difficulty
    switch (selectedCalibration.value) {
      case 'creator':
        difficulty = Math.round(item.creator_difficulty)
        break
      case 'psychometric':
        // Utiliser psychométrie si disponible, sinon créateur
        if (item.psychometric_reports > 0) {
          // Convertir taux de succès en difficulté (100% = niveau 1, 0% = niveau max)
          difficulty = Math.max(1, Math.min(levelsCount, 
            Math.round(levelsCount - (item.psychometric_success_rate / 100) * (levelsCount - 1))
          ))
        } else {
          difficulty = Math.round(item.creator_difficulty)
        }
        break
      case 'synthetic':
      default:
        difficulty = item.synthetic_difficulty
        break
    }
    
    // S'assurer que la difficulté est dans la plage valide
    difficulty = Math.max(1, Math.min(levelsCount, difficulty))
    
    organizedItems[difficulty].push({
      id: item._id,
      title: `${item.el_ID}`,
      type: item.el_type,
      contentPreview: item.el_text.length > 100 ? item.el_text.substring(0, 100) + '...' : item.el_text,
      difficulty: difficulty,
      catItemData: item, // Garder les données originales CAT
      metadata: {
        Created: new Date().toISOString().split('T')[0],
        Source: 'CAT Database',
        CalibrationMethod: selectedCalibration.value,
        OriginalCATId: item._id,
        UsageCount: item.usage_count,
        Confidence: item.confidence
      }
    })
  })
  
  // Statistiques de distribution
  const distributionStats = {}
  for (let i = 1; i <= levelsCount; i++) {
    distributionStats[i] = organizedItems[i].length
  }
  
  console.log('📊 Distribution of items by level:', distributionStats)
  
  // Stocker les données distribuées localement
  const distributionData = {
    items: organizedItems,
    calibrationMethod: selectedCalibration.value,
    totalItems: foundItems.value.length,
    distribution: distributionStats,
    missingItems: missingItems.value,
    searchCriteria: {
      ISCED: testStore?.testData?.ISCED,
      subject: testStore?.testData?.Subject,
      keywords: testStore?.testData?.Keywords
    }
  }
  
  distributedItemsData.value = distributionData
  
  // Émettre l'événement avec les items organisés
  emit('itemsDistributed', distributionData)
  
  // Notification de succès
  const totalDistributed = Object.values(distributionStats).reduce((sum, count) => sum + count, 0)
  alert(`✅ CAT Test Structure Ready!
  
📊 Your ${totalDistributed} items have been organized:
${Object.entries(distributionStats).map(([level, count]) => `Level ${level}: ${count} items`).join('\n')}

🎯 Next: Click "Create Test" to finalize your CAT test. Your real items from the Item Bank will be used (no fake items).`)
    
    // Activer la section de création de test
    isDistributed.value = true
    
  } catch (error) {
    console.error('❌ Error distributing items:', error)
    alert(`❌ Erreur lors de la distribution: ${error.message}`)
  } finally {
    isProcessing.value = false
  }
}

// Méthode finale pour créer le test CAT
const createCATTest = async () => {
  isCreatingTest.value = true
  
  try {
    console.log('🎯 Creating final CAT test with distributed items')
    
    // Vérifier que nous avons bien des données distribuées
    if (!isDistributed.value || !distributedItemsData.value) {
      alert('Please distribute the items first using "Prepare CAT Test Structure"')
      return
    }
    
    // Créer des données de test COMPLÈTEMENT NOUVELLES sans utiliser testStore
    const cleanTestData = createCleanCATTestData()
    
    console.log('📝 Auto-generated test title:', cleanTestData.title)
    console.log('📊 Distributed items data:', distributedItemsData.value)
    
    // Nettoyer les données distribuées de tout _id MongoDB
    const cleanDistributionData = cleanCATDistributionData(distributedItemsData.value)
    
    // Assembler les données finales du test CAT
    const finalTestData = {
      ...cleanTestData,
      CATParameters: {
        ...catParameters.value,
        itemsDistributed: true,
        lastDistribution: new Date().toISOString()
      },
      CATDistribution: cleanDistributionData
    }
    
    console.log('📤 Sending CAT test to API:', finalTestData)
    console.log('🔍 Final test data structure:', {
      hasId: !!finalTestData._id,
      ID: finalTestData.ID,
      title: finalTestData.title,
      topLevelKeys: Object.keys(finalTestData),
      CATDistributionKeys: finalTestData.CATDistribution ? Object.keys(finalTestData.CATDistribution) : null
    })
    
    // Vérification finale : aucun _id MongoDB ne doit être présent
    if (finalTestData._id) {
      console.error('❌ CRITICAL: MongoDB _id detected in final data!', finalTestData._id)
      delete finalTestData._id
    }
    
    // Envoyer directement au serveur
    const response = await api.post('/tests/addTest', finalTestData)
    
    if (response.status === 201) {
console.log(`✅ Test CAT créé avec succès !
📊 Test "${finalTestData.title}" créé avec :
- ${foundItems.value.length} items réels
- ${catParameters.value.paliers} niveaux
- Calibration: ${selectedCalibration.value}`)
      
      // Le message sera affiché par handleCATTestCreated
      
      // Réinitialiser le formulaire
      resetWizard()
      
      // Fermer le formulaire de création
      emit('cat-test-created', response.data)
      
    } else {
      throw new Error('Erreur lors de la création du test')
    }
    
  } catch (error) {
    console.error('❌ Error creating CAT test:', error)
    alert(`❌ Erreur lors de la création du test: ${error.message}`)
  } finally {
    isCreatingTest.value = false
  }
}

// Fonction utilitaire pour créer des données de test CAT complètement propres
const createCleanCATTestData = () => {
  // Utiliser les données du formulaire passées en props
  const formData = props.formData || {}
  const currentDate = new Date()
  const dateString = currentDate.toISOString().split('T')[0]
  const timeString = currentDate.getTime()
  
  console.log('📋 Using form data for CAT test creation:', {
    title: formData.title,
    ISCED: formData.ISCED,
    Subject: formData.Subject,
    domain: formData.domain,
    Description: formData.Description,
    Keywords: formData.Keywords,
    duration: formData.duration
  })
  
  // Traitement des Keywords (peut être string ou array)
  let processedKeywords = []
  if (formData.Keywords) {
    if (typeof formData.Keywords === 'string') {
      processedKeywords = formData.Keywords.split(',').map(k => k.trim()).filter(k => k)
    } else if (Array.isArray(formData.Keywords)) {
      processedKeywords = formData.Keywords
    }
  }
  // Ajouter CAT et adaptive aux keywords si pas déjà présents
  if (!processedKeywords.includes('CAT')) processedKeywords.push('CAT')
  if (!processedKeywords.includes('adaptive')) processedKeywords.push('adaptive')

  // Créer un objet complètement nouveau avec les vraies données du formulaire
  const cleanData = {
    ID: `cat_${timeString}_${crypto.randomUUID().replace(/-/g, '').substr(0, 12)}`,
    title: formData.title || `CAT-Test-${formData.ISCED || 'ISCED-2'}-${formData.Subject || 'general'}-${dateString}`,
    type: 'WASPL',
    progression: 'adaptive',
    Description: formData.Description || `Test CAT pour ${formData.Subject || 'general'} niveau ${formData.ISCED || 'ISCED-2'}`,
    Subject: formData.Subject || 'general',
    ISCED: formData.ISCED || 'ISCED-2',
    domain: formData.domain || '',
    Keywords: processedKeywords,
    language: formData.language || 'fr',
    duration: formData.duration || '',
    metadata: {
      Author: formData.metadata?.Author || 'CAT Wizard',
      Description: formData.Description || `Test CAT pour ${formData.Subject || 'general'} niveau ${formData.ISCED || 'ISCED-2'}`,
      Keywords: processedKeywords,
      Created: dateString,
      LastModif: currentDate.toISOString(),
      CreatedFrom: 'CAT_WIZARD'
    },
    pages: [{
      id: `page_${timeString}`,
      label: 'CAT Page 1',
      children: [],
      Randomized: true
    }],
    elements: [], // Sera rempli par le serveur
    settings: {
      timeLimit: formData.duration || '',
      shuffleQuestions: true,
      shufflePages: false,
      NavigationPanel: false,
      progressBar: true,
      feedbackAllowed: true,
      directScore: true,
      countDown: !!formData.duration // Activer countdown seulement si duration définie
    }
  }
  
  console.log('🧹 Created completely clean CAT test data:', {
    title: cleanData.title,
    ID: cleanData.ID,
    hasMongoId: !!cleanData._id,
    source: 'DOM_ELEMENTS_ONLY'
  })
  
  return cleanData
}

// Fonction utilitaire pour nettoyer les données CATDistribution
const cleanCATDistributionData = (distributionData) => {
  if (!distributionData || !distributionData.items) {
    return distributionData
  }
  
  const cleanedData = JSON.parse(JSON.stringify(distributionData)) // Deep clone
  
  // Nettoyer chaque niveau et chaque item
  Object.keys(cleanedData.items).forEach(levelKey => {
    cleanedData.items[levelKey].forEach(item => {
      // Préserver l'ID de l'Item Bank dans catItemData
      if (item._id && typeof item._id === 'string' && item._id.length === 24) {
        if (!item.catItemData) item.catItemData = {}
        item.catItemData._id = item._id
      }
      
      // Supprimer tous les _id au niveau de l'item pour éviter les conflits
      delete item._id
      
      // Nettoyer aussi les sous-objets
      if (item.metadata && item.metadata._id) {
        delete item.metadata._id
      }
    })
  })
  
  console.log('🧹 Cleaned CATDistribution data from MongoDB _id conflicts')
  return cleanedData
}


// Méthode pour afficher le titre qui sera utilisé pour le test
const getAutoGeneratedTitle = () => {
  const formData = props.formData || {}
  
  // Si un titre a été saisi manuellement, l'utiliser
  if (formData.title && formData.title.trim()) {
    return formData.title
  }
  
  // Sinon générer automatiquement
  const isced = formData.ISCED || 'ISCED-2'
  const subject = formData.Subject || 'general'
  const date = new Date().toISOString().split('T')[0]
  return `CAT-Test-${isced}-${subject}-${date}`
}

// Méthode pour réinitialiser l'assistant
const resetWizard = () => {
  isSearching.value = false
  itemsFound.value = false
  foundItems.value = []
  isCalibrated.value = false
  isDistributed.value = false
  isCreatingTest.value = false
  itemDistribution.value = {}
  missingItems.value = {}
  distributedItemsData.value = null
  
  // Réinitialiser les graphiques
  if (chartInstance) {
    chartInstance.destroy()
    chartInstance = null
  }
  if (missingChartInstance) {
    missingChartInstance.destroy()  
    missingChartInstance = null
  }
}

// Processus simplifié : plus besoin des fonctions rebuild et processItems
// La seule action disponible est maintenant validateAndDistribute() qui utilise automatiquement les items trouvés

// Calculer les items au montage
onMounted(() => {
  console.log("🚀 CATSimulator monté")
  calculateItems()
})
</script>

<style scoped>
.cat-simulator {
  background-color: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #dee2e6;
  margin-top: 15px;
}

.result-container {
  margin-top: 15px;
  text-align: center;
}

.alert-info {
  background-color: #d1ecf1;
  border-color: #b6d4da;
  color: #0c5460;
  padding: 12px;
  border-radius: 6px;
  border: 1px solid #b6d4da;
}

h4 {
  color: #495057;
  margin-bottom: 20px;
  text-align: center;
}

.form-floating>label {
  font-size: 0.875rem;
}

/* Styles pour la recherche et calibration */
.search-section {
  text-align: center;
}

/* Styles pour l'interface de critères de recherche */
.search-criteria-section {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 15px;
  margin: 15px 0;
}

.criteria-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-top: 15px;
}

.criteria-group h6 {
  color: #495057;
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 5px;
  margin-bottom: 10px;
}

.criteria-group .form-check {
  margin-bottom: 8px;
  padding-left: 1.5rem;
}

.criteria-group .form-check-label {
  font-size: 0.9rem;
  color: #495057;
}

.criteria-group .form-check-label small {
  font-size: 0.8rem;
  font-style: italic;
}

.keywords-list {
  max-height: 150px;
  overflow-y: auto;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  padding: 8px;
  background-color: white;
}

.keywords-list .form-check {
  margin-bottom: 5px;
}

@media (max-width: 768px) {
  .criteria-grid {
    grid-template-columns: 1fr;
    gap: 15px;
  }
}

.items-table-section {
  max-height: 400px;
  overflow-y: auto;
}

.items-table-section table {
  font-size: 0.9em;
}

.calibration-section {
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #dee2e6;
}

.chart-container, .stats-container {
  background-color: white;
  padding: 15px;
  border-radius: 8px;
  border: 1px solid #dee2e6;
}

.distribution-section {
  margin-top: 20px;
}

.missing-stats {
  background-color: white;
  padding: 15px;
  border-radius: 8px;
  border: 1px solid #dee2e6;
}

/* Badge personnalisé pour orange */
.bg-orange {
  background-color: #fd7e14 !important;
  color: white;
}

/* Animation pour le spinner */
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.search-results {
  animation: fadeIn 0.5s ease-in;
}

/* Styles pour les graphiques */
canvas {
  max-height: 300px;
}

/* Responsive pour les tableaux */
@media (max-width: 768px) {
  .items-table-section {
    font-size: 0.8em;
  }
  
  .table-responsive {
    font-size: 0.75em;
  }
}
</style>